import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { 
  Book, Heart, User, Shield, Play, Ear, FileText, AlertTriangle,
  HeartPulse, Star, HelpCircle, CheckCircle, ChevronRight, ChevronDown,
  Clock, Mail, Phone, ArrowLeft, BookOpen, Award
} from 'lucide-react';
import ObserverNav from '../components/ObserverNav';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const iconMap = {
  'heart': Heart,
  'user': User,
  'shield': Shield,
  'play': Play,
  'ear': Ear,
  'file-text': FileText,
  'alert-triangle': AlertTriangle,
  'heart-pulse': HeartPulse,
  'star': Star,
  'help-circle': HelpCircle
};

const ObserverHandbook = () => {
  const navigate = useNavigate();
  const [handbook, setHandbook] = useState(null);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedSection, setExpandedSection] = useState(null);
  const [markingRead, setMarkingRead] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('observer_token');
    if (!token) {
      navigate('/observer/login');
      return;
    }
    loadData();
  }, [navigate]);

  const loadData = async () => {
    try {
      const [handbookRes, progressRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/observer/handbook`),
        axios.get(`${BACKEND_URL}/api/observer/handbook/progress`, {
          params: { token: localStorage.getItem('observer_token') }
        })
      ]);
      
      setHandbook(handbookRes.data);
      setProgress(progressRes.data);
    } catch (error) {
      console.error('Error loading handbook:', error);
    } finally {
      setLoading(false);
    }
  };

  const markSectionRead = async (sectionId) => {
    setMarkingRead(sectionId);
    try {
      await axios.post(`${BACKEND_URL}/api/observer/handbook/mark-read`, null, {
        params: {
          token: localStorage.getItem('observer_token'),
          section_id: sectionId
        }
      });
      
      // Reload progress
      const progressRes = await axios.get(`${BACKEND_URL}/api/observer/handbook/progress`, {
        params: { token: localStorage.getItem('observer_token') }
      });
      setProgress(progressRes.data);
    } catch (error) {
      console.error('Error marking section read:', error);
    } finally {
      setMarkingRead(null);
    }
  };

  const markAllRead = async () => {
    setMarkingRead('all');
    try {
      await axios.post(`${BACKEND_URL}/api/observer/handbook/mark-read`, null, {
        params: {
          token: localStorage.getItem('observer_token'),
          section_id: 'all'
        }
      });
      
      const progressRes = await axios.get(`${BACKEND_URL}/api/observer/handbook/progress`, {
        params: { token: localStorage.getItem('observer_token') }
      });
      setProgress(progressRes.data);
    } catch (error) {
      console.error('Error marking all read:', error);
    } finally {
      setMarkingRead(null);
    }
  };

  const isSectionRead = (sectionId) => {
    return progress?.sections_read?.includes(sectionId) || progress?.sections_read?.includes('all');
  };

  const formatContent = (content) => {
    // Convert markdown-like formatting to JSX
    const lines = content.split('\n');
    return lines.map((line, idx) => {
      // Bold text
      line = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      
      // Bullet points
      if (line.trim().startsWith('•') || line.trim().startsWith('-')) {
        return (
          <li key={idx} className="ml-4 text-gray-700" dangerouslySetInnerHTML={{ __html: line.replace(/^[•-]\s*/, '') }} />
        );
      }
      
      // Red flag items
      if (line.trim().startsWith('🔴')) {
        return (
          <div key={idx} className="flex items-start gap-2 py-1">
            <span className="text-red-500">🔴</span>
            <span className="text-gray-700" dangerouslySetInnerHTML={{ __html: line.replace('🔴', '') }} />
          </div>
        );
      }
      
      // Star items
      if (line.trim().startsWith('⭐')) {
        return (
          <div key={idx} className="flex items-start gap-2 py-1 bg-yellow-50 px-3 rounded">
            <Star className="w-4 h-4 text-yellow-500 mt-0.5" />
            <span className="text-gray-700" dangerouslySetInnerHTML={{ __html: line.replace('⭐', '') }} />
          </div>
        );
      }
      
      // Numbered items
      if (/^\d+\./.test(line.trim())) {
        return (
          <li key={idx} className="ml-4 text-gray-700 list-decimal" dangerouslySetInnerHTML={{ __html: line.replace(/^\d+\.\s*/, '') }} />
        );
      }
      
      // Q&A format
      if (line.trim().startsWith('**Q:')) {
        return (
          <div key={idx} className="mt-4 font-semibold text-violet-800" dangerouslySetInnerHTML={{ __html: line }} />
        );
      }
      if (line.trim().startsWith('A:')) {
        return (
          <div key={idx} className="mb-2 text-gray-700 pl-4 border-l-2 border-violet-200" dangerouslySetInnerHTML={{ __html: line.replace('A:', '') }} />
        );
      }
      
      // Empty lines
      if (!line.trim()) {
        return <div key={idx} className="h-2" />;
      }
      
      // Regular paragraph
      return <p key={idx} className="text-gray-700 my-1" dangerouslySetInnerHTML={{ __html: line }} />;
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <ObserverNav />
        <div className="pt-20 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <ObserverNav />
      
      <div className="pt-20 pb-8 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Book className="w-7 h-7 text-violet-600" />
                Observer Handbook
              </h1>
              <p className="text-gray-600">Your guide to being an effective Observer</p>
            </div>
            <Button
              variant="ghost"
              onClick={() => navigate('/observer/dashboard')}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          </div>

          {/* Progress Card */}
          {progress && (
            <Card className="mb-6 border-l-4 border-l-violet-500">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                      progress.completed ? 'bg-green-100' : 'bg-violet-100'
                    }`}>
                      {progress.completed ? (
                        <Award className="w-6 h-6 text-green-600" />
                      ) : (
                        <BookOpen className="w-6 h-6 text-violet-600" />
                      )}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">
                        {progress.completed ? 'Handbook Completed!' : 'Reading Progress'}
                      </p>
                      <p className="text-sm text-gray-500">
                        {progress.read_sections} of {progress.total_sections} sections read
                      </p>
                    </div>
                  </div>
                  {!progress.completed && (
                    <Button
                      onClick={markAllRead}
                      disabled={markingRead === 'all'}
                      variant="outline"
                      className="text-violet-600 border-violet-300"
                    >
                      {markingRead === 'all' ? 'Marking...' : 'Mark All Read'}
                    </Button>
                  )}
                </div>
                <Progress value={progress.completion_percentage} className="h-2" />
                <p className="text-right text-sm text-gray-500 mt-2">{progress.completion_percentage}% complete</p>
              </CardContent>
            </Card>
          )}

          {/* Quick Reference */}
          {handbook?.quick_reference && (
            <Card className="mb-6 bg-gradient-to-r from-violet-50 to-indigo-50">
              <CardContent className="p-6">
                <h3 className="font-semibold text-violet-900 mb-4 flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  {handbook.quick_reference.title}
                </h3>
                <div className="grid grid-cols-3 gap-4">
                  {handbook.quick_reference.items.map((item, idx) => (
                    <div key={idx} className="bg-white rounded-lg p-3 text-center shadow-sm">
                      <p className="text-xs text-gray-500">{item.label}</p>
                      <p className="font-bold text-violet-700">{item.value}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Handbook Sections */}
          <div className="space-y-4">
            {handbook?.sections?.map((section) => {
              const Icon = iconMap[section.icon] || HelpCircle;
              const isExpanded = expandedSection === section.id;
              const isRead = isSectionRead(section.id);
              
              return (
                <Card 
                  key={section.id}
                  className={`overflow-hidden transition-all ${
                    isExpanded ? 'ring-2 ring-violet-300' : ''
                  } ${isRead ? 'border-l-4 border-l-green-500' : ''}`}
                >
                  {/* Section Header */}
                  <button
                    onClick={() => setExpandedSection(isExpanded ? null : section.id)}
                    className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        isRead ? 'bg-green-100' : 'bg-violet-100'
                      }`}>
                        {isRead ? (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        ) : (
                          <Icon className="w-5 h-5 text-violet-600" />
                        )}
                      </div>
                      <div className="text-left">
                        <p className="font-semibold text-gray-900">{section.title}</p>
                        {isRead && <p className="text-xs text-green-600">Completed</p>}
                      </div>
                    </div>
                    {isExpanded ? (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    )}
                  </button>
                  
                  {/* Section Content */}
                  {isExpanded && (
                    <CardContent className="px-6 pb-6 pt-0 border-t">
                      <div className="prose prose-violet max-w-none mt-4">
                        {formatContent(section.content)}
                      </div>
                      
                      {!isRead && (
                        <div className="mt-6 pt-4 border-t flex justify-end">
                          <Button
                            onClick={() => markSectionRead(section.id)}
                            disabled={markingRead === section.id}
                            className="bg-violet-600 hover:bg-violet-700 text-white"
                          >
                            {markingRead === section.id ? (
                              'Marking...'
                            ) : (
                              <>
                                <CheckCircle className="w-4 h-4 mr-2" />
                                Mark as Read
                              </>
                            )}
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  )}
                </Card>
              );
            })}
          </div>

          {/* Emergency Contacts */}
          {handbook?.emergency_contacts && (
            <Card className="mt-6 bg-red-50 border-red-200">
              <CardContent className="p-6">
                <h3 className="font-semibold text-red-900 mb-4 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5" />
                  {handbook.emergency_contacts.title}
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  {handbook.emergency_contacts.contacts.map((contact, idx) => (
                    <div key={idx} className="bg-white rounded-lg p-3 flex items-center gap-3">
                      {contact.type === 'email' ? (
                        <Mail className="w-5 h-5 text-red-500" />
                      ) : contact.type === 'phone' ? (
                        <Phone className="w-5 h-5 text-red-500" />
                      ) : (
                        <AlertTriangle className="w-5 h-5 text-red-500" />
                      )}
                      <div>
                        <p className="text-sm text-gray-500">{contact.name}</p>
                        <p className="font-medium text-gray-900">{contact.value}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Footer Note */}
          <div className="mt-6 text-center text-sm text-gray-500">
            <p>Version {handbook?.version} • Last updated: {handbook?.last_updated}</p>
            <p className="mt-1">Questions? Contact <a href="mailto:observers@sanjaya.com" className="text-violet-600 hover:underline">observers@sanjaya.com</a></p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ObserverHandbook;
